import { useRef, type ComponentType } from 'react';
import { createFileRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { Capacitor } from '@capacitor/core';

import { COLLECTION_ROUTE, HOME_ROUTE, MY_RECO_ROUTE, PROFILE_ROUTE } from '@/constants/routes';
import { useUiSignals } from '@/stores/uiSignals';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useRefreshCirclesOnResume } from '@/hooks/useRefreshCirclesOnResume';
import NotificationPermissionModal from '@/components/NotificationPermissionModal';
import DefaultTabBar from '@/components/navigation/DefaultTabBar';
import IosTabBar from '@/components/navigation/IosTabBar';
import KeepAliveTabs from '@/components/navigation/KeepAliveTabs';
import { Route as HomeRoute } from './_tabs/home';
import { Route as MyRecoRoute } from './_tabs/my-reco';
import { Route as CollectionRoute } from './_tabs/collection';
import { Route as ProfileRoute } from './_tabs/profile';

/**
 * Pathless tabs layout — the screens and the bottom tab bar shared by every
 * tab screen (Découvre · Mes reco · [🎵] · Collection · Profil).
 *
 * ios-tab-bar — on iOS the bar floats over the content and the four tab
 * screens stay alive between visits (`KeepAliveTabs`); the 🎵 reco flow still
 * goes through the Outlet. Android and the web keep the docked bar and the
 * Outlet. A phase-2 Android bar = one more branch here.
 */
export const Route = createFileRoute('/_tabs')({
  component: TabsLayout,
});

/** Tab screens kept alive on iOS — the route components themselves, routes unchanged. */
const TAB_SCREENS = {
  [HOME_ROUTE]: HomeRoute.options.component,
  [MY_RECO_ROUTE]: MyRecoRoute.options.component,
  [COLLECTION_ROUTE]: CollectionRoute.options.component,
  [PROFILE_ROUTE]: ProfileRoute.options.component,
} as Record<string, ComponentType>;

function TabsLayout() {
  // Push bootstrap (native only; no-op on web): silent re-register when
  // already granted, or first-time soft-ask modal before the OS prompt.
  const { showPrompt, accept, decline } = usePushNotifications();
  // circle-activity-order — the circle list follows activity: reload it on return.
  useRefreshCirclesOnResume();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const bumpHomeScrollTop = useUiSignals((s) => s.bumpHomeScrollTop);
  const lastHomeTap = useRef(0);
  const ios = Capacitor.getPlatform() === 'ios';

  // Double-tap the active Découvre tab → scroll Home to the top.
  const onHomeTap = () => {
    if (pathname !== HOME_ROUTE) return;
    const now = Date.now();
    if (now - lastHomeTap.current < 350) bumpHomeScrollTop();
    lastHomeTap.current = now;
  };

  const modal = (
    <NotificationPermissionModal open={showPrompt} onAccept={accept} onDecline={decline} />
  );

  if (ios) {
    return (
      // --tab-bar-inset: the bar (62 px) + its gap to the screen bottom + 8 px of air —
      // what each screen keeps free under its content.
      <div className="tw:relative tw:flex tw:h-full tw:flex-col tw:overflow-hidden tw:[--tab-bar-inset:calc(70px+max(12px,calc(env(safe-area-inset-bottom)-14px)))]">
        <main className="tw:flex-1 tw:overflow-hidden">
          <KeepAliveTabs active={pathname} screens={TAB_SCREENS} fallback={<Outlet />} />
        </main>
        {modal}
        <IosTabBar pathname={pathname} onHomeTap={onHomeTap} />
      </div>
    );
  }

  return (
    <div className="tw:flex tw:h-full tw:flex-col tw:overflow-hidden">
      <main className="tw:flex-1 tw:overflow-hidden">
        <Outlet />
      </main>

      {modal}

      <DefaultTabBar pathname={pathname} onHomeTap={onHomeTap} />
    </div>
  );
}
